import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  FormControl,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useForm, FormProvider } from 'react-hook-form';
import { TextFieldController } from '../../../components/organisms/form/controllers/TextFieldController';
import { SelectController } from '../../../components/organisms/form/controllers/SelectController';
import { BasicTableHead, BasicTableHeadProps } from '../../../components/molecules';
import { MenuHeaderTemplate } from '../../../components/templates/MenuHeaderTemplate';
import { Search } from '@material-ui/icons';
import { ReadMoreButton } from '../../../components/organisms/general/ReadMoreButton';
import { ListCouponPackMasters, useListCouponPackMasters } from './hooks/useListCouponPackMasters';
import { formatJstDate, formatJstDateTime } from '../../../util';
import { CouponPackMaster } from '../../../graphql/generated';
import { OrganizationContext } from '../../../components/contexts/organization';

const useStyles = makeStyles((theme: Theme) => ({
  conditionFormControl: {
    minWidth: 150,
    maxWidth: 300,
  },
  resultList: {
    marginTop: theme.spacing(4),
  },
  btnSearch: {
    marginTop: theme.spacing(2),
  },
}));

export type SearchFormValues = {
  value: string;
  searchCondition: string;
};

export const CouponPackMasterListBody: React.FC<ListCouponPackMasters> = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const orgContext = useContext(OrganizationContext);

  const searchForm = useForm<SearchFormValues>({
    defaultValues: {
      value: '',
      searchCondition: 'none',
    },
  });

  const { handleSubmit, watch } = searchForm;
  const watchSearchCondition = watch('searchCondition', 'none');

  const tableHeadProps: BasicTableHeadProps = {
    columns: [
      {
        name: 'couponCode',
        label: 'コード',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'couponName',
        label: '回数券名',
        minWidth: theme.spacing(30),
        align: 'left',
      },
      {
        name: 'validityPeriod',
        label: '表示期間',
        minWidth: theme.spacing(18),
        align: 'left',
      },
      {
        name: 'availablePeriod',
        label: '有効期限',
        minWidth: theme.spacing(21),
        align: 'left',
      },
      {
        name: 'registrationDate',
        label: '更新日時/作成日時',
        minWidth: theme.spacing(22),
        align: 'left',
      },
      {
        name: 'action',
        label: '',
        minWidth: theme.spacing(5),
        align: 'center',
      },
    ],
  };

  const formatDisplayDays = (row: CouponPackMaster): React.ReactElement => {
    return (
      <>
        {formatJstDate(row.displayStartDate)}から
        <br />
        {formatJstDate(row.displayEndDate)}まで
      </>
    );
  };

  const formatValidityDays = (row: CouponPackMaster): React.ReactElement => {
    return (
      <>
        {row.validityStartDateTime ? `${formatJstDateTime(row.validityStartDateTime)} から` : ''}
        {row.validityStartDateTime ? <br /> : <></>}
        {`${formatJstDateTime(row.validityEndDateTime)} まで`}
      </>
    );
  };

  const renderSearchForm = (): React.ReactElement => {
    switch (watchSearchCondition) {
      case 'couponName':
        return (
          <Box width={'300px'}>
            <TextFieldController
              name="value"
              label="回数券名が"
              variant="standard"
              rule={{
                required: {
                  value: true,
                  message: '条件を指定する場合、必須です',
                },
                maxLength: {
                  value: 30,
                  message: '30文字以内で入力してください',
                },
              }}
            />
            <Grid item xs={12} className={classes.btnSearch}>
              <Button
                type="submit"
                variant="outlined"
                size="large"
                color={'default'}
                disabled={props.loading}
                endIcon={<Search />}
                fullWidth
              >
                検索
              </Button>
            </Grid>
          </Box>
        );
      case 'couponCode':
        return (
          <Box width={'300px'}>
            <TextFieldController
              name="value"
              label="回数券コードが"
              variant="standard"
              rule={{
                required: {
                  value: true,
                  message: '条件を指定する場合、必須です',
                },
                maxLength: {
                  value: 30,
                  message: '30文字以内で入力してください',
                },
              }}
            />
            <Grid item xs={12} className={classes.btnSearch}>
              <Button
                type="submit"
                variant="outlined"
                size="large"
                color={'default'}
                disabled={props.loading}
                endIcon={<Search />}
                fullWidth
              >
                検索
              </Button>
            </Grid>
          </Box>
        );
      default:
        // and none
        return (
          <Box width={'300px'} className={classes.btnSearch}>
            <Button
              type="submit"
              variant="outlined"
              size="large"
              color={'default'}
              disabled={props.loading}
              endIcon={<Search />}
              fullWidth
            >
              検索
            </Button>
          </Box>
        );
    }
  };

  return (
    <MenuHeaderTemplate
      header={{
        title: '回数券マスタ一覧',
        loading: undefined,
        progressLabel: undefined,
      }}
    >
      <Box px={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Breadcrumbs>
            <Typography variant="body1">{orgContext.organization?.name}回数券マスタ一覧</Typography>
          </Breadcrumbs>
          <Button
            color="secondary"
            variant="contained"
            onClick={() =>
              history.push({
                pathname: '/coupon-pack-masters/new',
                search: history.location.search,
              })
            }
          >
            <Typography variant="body1">新規作成</Typography>
          </Button>
        </Box>
        <Grid container>
          <Grid item xs={12}>
            <FormProvider {...searchForm}>
              <form onSubmit={handleSubmit(props.onSearchCouponPackMasters)} autoComplete="off" noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl className={classes.conditionFormControl}>
                      <SelectController
                        name="searchCondition"
                        label="検索条件"
                        disabled={props.loading}
                        variant="standard"
                        menuItems={[
                          { value: 'none', label: 'なし' },
                          { value: 'couponName', label: '回数券名' },
                          { value: 'couponCode', label: '回数券コード' },
                        ]}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid item>{renderSearchForm()}</Grid>
              </form>
            </FormProvider>
          </Grid>
          <Grid item xs={12} className={classes.resultList}>
            <Box>
              <TableContainer component={Paper}>
                {props.loading ? <LinearProgress /> : <React.Fragment />}
                <Table>
                  <BasicTableHead columns={tableHeadProps.columns} />
                  <TableBody>
                    {(() => {
                      if (!props.data || !props.data.couponPackMasters) {
                        return;
                      } else if (
                        props.data.couponPackMasters.nodes &&
                        props.data.couponPackMasters.nodes.length === 0
                      ) {
                        return (
                          <TableRow>
                            <TableCell>データがありません</TableCell>
                          </TableRow>
                        );
                      } else {
                        return props.data.couponPackMasters.nodes.map((record, index) => (
                          <TableRow key={index}>
                            <TableCell
                              component="th"
                              scope="row"
                              style={{
                                wordBreak: 'break-all',
                              }}
                            >
                              {record.couponCode}
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              style={{
                                wordBreak: 'break-all',
                              }}
                            >
                              {record.couponName}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <Typography variant={'caption'}>{formatDisplayDays(record)}</Typography>
                            </TableCell>
                            <TableCell component="th" scope="record">
                              <Typography variant={'caption'}> {formatValidityDays(record)} </Typography>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <Typography variant={'caption'}>
                                更新：{formatJstDateTime(record.updateAtDateTime || '')}
                              </Typography>
                              <br />
                              <Typography variant={'caption'}>
                                作成：{formatJstDateTime(record.createAtDateTime || '')}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" scope="row">
                              <Button
                                onClick={() =>
                                  history.push({
                                    pathname: `/coupon-pack-masters/${record.id}`,
                                    search: history.location.search,
                                  })
                                }
                                type="button"
                                size="small"
                                color="primary"
                              >
                                詳細
                              </Button>
                            </TableCell>
                          </TableRow>
                        ));
                      }
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
        <Box mt={2}>
          {props.data?.couponPackMasters.pageInfo.nextToken ? (
            <ReadMoreButton fetchMore={props.onFetchMore} loading={props.loading} />
          ) : (
            <></>
          )}
        </Box>
      </Box>
    </MenuHeaderTemplate>
  );
};

export const CouponPackMasterListPage: React.FC = () => {
  const listCouponPackMastersPage = useListCouponPackMasters();
  return <CouponPackMasterListBody {...listCouponPackMastersPage} />;
};
